import { useCallback, memo, useRef, useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import './ValidateDetails.scss';
import CustomSelect from '../../CommonComponents/CustomSelect';
import CustomToolTip from '../../CommonComponents/CustomToolTip';
import ConfirmationDialog from '../../CommonComponents/confirmation_dialog/ConfirmationDialog';
import CustomFilter from '../../CommonComponents/custom_filter/CustomFilter';
import { FilterSvg, PlusIconSized, SatelliteDropdownIcon } from '../../CommonComponents/svgs/Svgs';
import { SESSION_STATUS_LABEL, VALIDATE_HEADER_SELECT } from '../constants/validateDetails.constants';
import { getEmptySessionsMessage, getSessionStatusBadgeClass, setupHasRunningSession } from '../helpers/validateDetails.helpers';
import type { ValidateSession, ValidateSetup } from '../types/validateDetails.types';
import { ValidateProvider, useValidateState, useValidateActions } from '../context/ValidateContext';
import CreateSetupModal from './CreateSetupModal';
import CreateSessionModal from './CreateSessionModal';
import SubsystemDiagramModal from './SubsystemDiagramModal';
import useUrlParams from '../../command_center/hooks/useUrlParams';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { navigateTo } from '../../../utils/commonFunctions';
import type { MissionStore } from '../../../store/MissionSlice';
import { downloadSessionReport, getSetupSubsystemDiagram } from '../api/validate.api';
import type { ReportFormat } from '../api/validate.api';
import EmptyPage from '../../command_center/custom_components/EmptyPage';
import { NoSchedulePreview } from '../../command_center/components/center/planning/Svgs';
import { StatusSvg } from '../../simulate/svgs/Svgs';

// ─── Shared helpers ───────────────────────────────────────────────────────────

type MenuPos = { top?: number; bottom?: number; right: number };

/** Compute fixed-position coordinates for a portal menu anchored to `el`. */
function calcPortalPos(el: HTMLElement, estimatedHeight: number): MenuPos {
  const rect  = el.getBoundingClientRect();
  const right = window.innerWidth - rect.right;
  return window.innerHeight - rect.bottom < estimatedHeight
    ? { bottom: (window.innerHeight - rect.top) + 6, right }
    : { top: rect.bottom + 6, right };
}

/** Close a portal menu when clicking outside or pressing Escape. */
function useMenuDismiss(
  open: boolean,
  onClose: () => void,
  menuRef: { current: Element | null },
  excludeRef?: { current: Element | null },
) {
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const inMenu    = menuRef.current?.contains(e.target as Node);
      const inExclude = excludeRef?.current?.contains(e.target as Node);
      if (!inMenu && !inExclude) onClose();
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onClose, menuRef, excludeRef]);
}

/** Portalled menu container shared by the actions and export menus. */
interface PortalMenuProps {
  menuRef: React.RefObject<HTMLDivElement>;
  pos: MenuPos;
  children: React.ReactNode;
}

function PortalMenu({ menuRef, pos, children }: PortalMenuProps) {
  return createPortal(
    <div
      ref={menuRef}
      className="validate__actions-menu"
      role="menu"
      style={{ ...pos, position: 'fixed' }}
      onClick={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  );
}

function SetupCardActionsSvg() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M11.71 12.6639L11.71 8.77783" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M11.7129 4.5L11.7129 3" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M9.71289 6.66406C9.71289 7.76863 10.6083 8.66406 11.7129 8.66406C12.8175 8.66406 13.7129 7.76863 13.7129 6.66406C13.7129 5.55949 12.8175 4.66406 11.7129 4.66406C10.6083 4.66406 9.71289 5.55949 9.71289 6.66406Z" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.04102 2.99972L5.04102 6.88574" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M5.04102 11.1641L5.04102 12.6641" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7.04102 9C7.04102 7.89543 6.14559 7 5.04102 7C3.93645 7 3.04102 7.89543 3.04102 9C3.04102 10.1046 3.93645 11 5.04102 11C6.14559 11 7.04102 10.1046 7.04102 9Z" stroke="currentColor" strokeWidth="0.533333" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ─── SetupCardActionsMenu ────────────────────────────────────────────────────

interface SetupCardActionsMenuProps {
  setup: ValidateSetup;
  isSetupBusy: boolean;
  onOpenDiagram: (setup: ValidateSetup) => void;
}

const SetupCardActionsMenu = memo(function SetupCardActionsMenu({
  setup,
  isSetupBusy,
  onOpenDiagram,
}: SetupCardActionsMenuProps) {
  const { onSetupDeleteOpen } = useValidateActions();

  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<MenuPos>({ top: 0, right: 0 });

  const close = useCallback(() => setOpen(false), []);
  useMenuDismiss(open, close, menuRef, btnRef);

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    setPos(calcPortalPos(btnRef.current, 96));
    setOpen(true);
  }, []);

  const handleOpenDiagram = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    close();
    onOpenDiagram(setup);
  }, [close, onOpenDiagram, setup]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSetupBusy) return;
    close();
    onSetupDeleteOpen(setup);
  }, [close, isSetupBusy, onSetupDeleteOpen, setup]);

  return (
    <>
      <CustomToolTip title="Setup actions" placement="top">
        <button
          ref={btnRef}
          className={`validate__card__actions-btn${open ? ' validate__card__actions-btn--active' : ''}`}
          type="button"
          aria-label={`Setup actions for ${setup.setupName}`}
          aria-haspopup="menu"
          aria-expanded={open}
          onClick={openMenu}
          data-testid={`setup-actions-btn-${setup.setupId}`}
        >
          <SetupCardActionsSvg />
        </button>
      </CustomToolTip>

      {open && (
        <PortalMenu menuRef={menuRef} pos={pos}>
          <button
            className="validate__actions-menu__item"
            role="menuitem"
            type="button"
            onClick={handleOpenDiagram}
            data-testid={`setup-diagram-btn-${setup.setupId}`}
          >
            View Diagram
          </button>
          <div className="validate__actions-menu__divider" aria-hidden />
          <button
            className={`validate__actions-menu__item${isSetupBusy ? ' validate__actions-menu__item--disabled' : ''}`}
            role="menuitem"
            type="button"
            disabled={isSetupBusy}
            aria-disabled={isSetupBusy}
            onClick={handleDelete}
            data-testid={`setup-delete-btn-${setup.setupId}`}
          >
            Delete
          </button>
        </PortalMenu>
      )}
    </>
  );
});

// ─── SessionActionsMenu ───────────────────────────────────────────────────────

interface SessionActionsMenuProps {
  session: ValidateSession;
}

const SessionActionsMenu = memo(function SessionActionsMenu({ session }: SessionActionsMenuProps) {
  const { onDeleteOpen, onEndSessionOpen } = useValidateActions();
  const { mission_id } = useUrlParams();

  const [open, setOpen]             = useState(false);
  const btnRef                      = useRef<HTMLButtonElement>(null);
  const menuRef                     = useRef<HTMLDivElement>(null);
  const [pos, setPos]               = useState<MenuPos>({ top: 0, right: 0 });

  const [exportOpen, setExportOpen] = useState(false);
  const exportMenuRef               = useRef<HTMLDivElement>(null);
  const [exportPos, setExportPos]   = useState<MenuPos>({ top: 0, right: 0 });

  const isDeploying = session.status === 'DEPLOYING';
  const isRunning   = session.status === 'RUNNING';
  const isCompleted = session.status === 'COMPLETED';
  const isSetupDeleted = Boolean(session.setupIsDeleted);
  const isDeleting = Boolean(session.isDeleting);
  const canEnd      = isRunning && !isDeleting;
  const canDelete   = !isDeleting && (isSetupDeleted || (!isRunning && !isDeploying));
  const canExport   = !isDeleting && (isRunning || isCompleted);

  const close       = useCallback(() => setOpen(false), []);
  const closeExport = useCallback(() => setExportOpen(false), []);

  useMenuDismiss(open,       close,       menuRef,       btnRef);
  useMenuDismiss(exportOpen, closeExport, exportMenuRef);

  const openMenu = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!btnRef.current) return;
    setPos(calcPortalPos(btnRef.current, 160));
    setOpen(true);
  }, []);

  const handleExportAll = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!btnRef.current) { close(); return; }
    setExportPos(calcPortalPos(btnRef.current, 120));
    close();
    setExportOpen(true);
  }, [close]);

  const handleExportFormat = useCallback((format: ReportFormat) => {
    setExportOpen(false);
    if (!mission_id) return;
    downloadSessionReport(mission_id, session.sessionId, format);
  }, [mission_id, session.sessionId]);

  const handleEndSession = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canEnd) return;
    onEndSessionOpen(session);
    close();
  }, [canEnd, onEndSessionOpen, session, close]);

  const handleDelete = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (!canDelete) return;
    onDeleteOpen(session);
    close();
  }, [canDelete, onDeleteOpen, session, close]);

  return (
    <>
      <button
        ref={btnRef}
        className={`validate__actions-btn${open ? ' validate__actions-btn--active' : ''}${isDeleting ? ' validate__actions-btn--disabled' : ''}`}
        type="button"
        aria-label="Session actions"
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={isDeleting}
        onClick={openMenu}
        data-testid={`session-actions-btn-${session.sessionId}`}
      >
        <span className="validate__actions-btn__dots" aria-hidden>
          <span /><span /><span />
        </span>
      </button>

      {open && (
        <PortalMenu menuRef={menuRef} pos={pos}>
          <button
            className={`validate__actions-menu__item${!canExport ? ' validate__actions-menu__item--disabled' : ''}`}
            role="menuitem"
            type="button"
            disabled={!canExport}
            aria-disabled={!canExport}
            onClick={handleExportAll}
            data-testid={`session-export-btn-${session.sessionId}`}
          >
            Export All Reports
          </button>
          <button
            className={`validate__actions-menu__item validate__actions-menu__item--end${!canEnd ? ' validate__actions-menu__item--disabled' : ''}`}
            role="menuitem"
            type="button"
            disabled={!canEnd}
            aria-disabled={!canEnd}
            onClick={handleEndSession}
            data-testid={`session-end-btn-${session.sessionId}`}
          >
            End Session
          </button>
          <div className="validate__actions-menu__divider" aria-hidden />
          <button
            className={`validate__actions-menu__item${!canDelete ? ' validate__actions-menu__item--disabled' : ''}`}
            role="menuitem"
            type="button"
            disabled={!canDelete}
            aria-disabled={!canDelete}
            onClick={handleDelete}
            data-testid={`session-delete-btn-${session.sessionId}`}
          >
            Delete
          </button>
        </PortalMenu>
      )}

      {exportOpen && (
        <PortalMenu menuRef={exportMenuRef} pos={exportPos}>
          <button className="validate__actions-menu__item" role="menuitem" type="button"
            onClick={() => handleExportFormat('pdf-executive')}>
            Summary Report (.pdf)
          </button>
          <button className="validate__actions-menu__item" role="menuitem" type="button"
            onClick={() => handleExportFormat('pdf-complete')}>
            Detailed Report (.pdf)
          </button>
          <button className="validate__actions-menu__item" role="menuitem" type="button"
            onClick={() => handleExportFormat('excel')}>
            Full Report (.xlsx)
          </button>
        </PortalMenu>
      )}
    </>
  );
});

// ─── SessionRow ───────────────────────────────────────────────────────────────

interface SessionRowProps {
  session: ValidateSession;
  nowMs: number;
}

function formatElapsedMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hh = Math.floor(totalSec / 3600);
  const mm = Math.floor((totalSec % 3600) / 60);
  const ss = totalSec % 60;

  if (totalSec < 3600) {
    return `${Math.floor(totalSec / 60)}m${String(ss).padStart(2, '0')}s`;
  }
  return `${hh}h ${mm}m ${ss}s`;
}

const SessionRow = memo(function SessionRow({ session, nowMs }: SessionRowProps) {
  const { onOpenRunningSession } = useValidateActions();
  const isRunning    = session.status === 'RUNNING';
  const isCompleted  = session.status === 'COMPLETED';
  const isDeploying  = session.status === 'DEPLOYING';
  const isSetupDeleted = Boolean(session.setupIsDeleted);
  const isDeleting   = Boolean(session.isDeleting);
  const badgeClass   = isDeleting ? 'ending' : getSessionStatusBadgeClass(session.status);
  const isClickable  = !isDeleting && (isRunning || isCompleted);
  const handleOpen   = useCallback(() => {
    if (!isClickable) return;
    onOpenRunningSession(session);
  }, [isClickable, onOpenRunningSession, session]);
  const rowStateClass = isDeleting
    ? ' validate__table__row--deleting'
    : isRunning
    ? ' validate__table__row--running'
    : isDeploying
      ? ' validate__table__row--deploying'
      : '';
  const setupTooltip = isSetupDeleted
    ? `${session.setupName || 'Setup'} setup has been deleted`
    : session.setupName || '—';
  const statusTooltip = isDeleting
    ? 'Ending Session'
    : isRunning
    ? 'Click to View Running Session'
    : isCompleted
      ? 'Click to View Summary'
      : isDeploying
        ? 'Deployment In Progress'
        : '';
  const rowAriaLabel = isClickable
    ? `${isSetupDeleted ? 'Setup deleted. ' : ''}${statusTooltip} ${session.sessionName}`
    : isSetupDeleted
      ? `${session.sessionName}. Setup deleted`
      : undefined;

  const durationLabel = useMemo(() => {
    if (isRunning || isDeploying || isDeleting) {
      const startMs = Date.parse(session.startedAt ?? '');
      if (!Number.isFinite(startMs)) return '—';
      return formatElapsedMs(nowMs - startMs);
    }
    if (typeof session.durationMinutes === 'number' && Number.isFinite(session.durationMinutes)) {
      return `${session.durationMinutes} Min`;
    }
    return '—';
  }, [isRunning, isDeploying, isDeleting, session.startedAt, session.durationMinutes, nowMs]);

  const handleStatusAction = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    handleOpen();
  }, [handleOpen]);

  return (
    <div
      className={`validate__table__row${isClickable ? ' validate__table__row--clickable' : ''}${rowStateClass}${isSetupDeleted ? ' validate__table__row--setup-deleted' : ''}`}
      role="row"
      onClick={isClickable ? handleOpen : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === 'Enter') handleOpen(); } : undefined}
      aria-label={rowAriaLabel}
      data-testid={`session-row-${session.sessionId}`}
    >
      <div className="validate__table__td validate__table__td--left-cap" role="cell">
        <CustomToolTip title={session.sessionName} placement="top" className="validate__session-name-tooltip">
          <span className="validate__text validate__text--md validate__session-name" data-testid={`session-name-${session.sessionId}`}>
            {session.sessionName}
          </span>
        </CustomToolTip>
      </div>
      <div className="validate__table__td" role="cell">
        <span className="validate__text validate__text--sm">{session.startedAt}</span>
      </div>
      <div className="validate__table__td" role="cell">
        <div className="validate__duration__cell">
          <span className="validate__text validate__text--sm">{durationLabel}</span>
        </div>
      </div>
      <div className="validate__table__td" role="cell">
        <span className="validate__text validate__text--sm validate__text--env">
          {session.testingEnvironment || '—'}
        </span>
      </div>
      <div className="validate__table__td" role="cell">
        <CustomToolTip title={setupTooltip} placement="top" className="validate__setup-name-tooltip">
          <span className="validate__setup-cell">
            <span className="validate__text validate__text--sm validate__setup-name">
              {session.setupName || '—'}
            </span>
            {isSetupDeleted && <span className="validate__setup-deleted-badge">Setup deleted</span>}
          </span>
        </CustomToolTip>
      </div>
      <div className="validate__table__td" role="cell">
        <CustomToolTip title={session.teamName} placement="top" className="validate__team-badge-tooltip">
          <span className="validate__team-badge">{session.teamName}</span>
        </CustomToolTip>
      </div>
      <div className="validate__table__td validate__table__td--status" role="cell">
        <div className="validate__status-cell">
          <span className={`validate__status-badge validate__status-badge--${badgeClass}`} data-testid={`session-status-${session.sessionId}`}>
            <span className="validate__status-badge__dot" />
            <span className="validate__status-badge__label">{isDeleting ? 'Ending...' : SESSION_STATUS_LABEL[session.status]}</span>
          </span>
          <CustomToolTip title={statusTooltip} placement="top" disabled={!statusTooltip}>
            {isClickable ? (
              <button
                className={`validate__status-action validate__status-action--${badgeClass}`}
                type="button"
                aria-label={statusTooltip}
                onClick={handleStatusAction}
              >
                {isRunning ? (
                  <span className="validate__status-action__spinner validate__status-action__spinner--running" />
                ) : (
                  <StatusSvg status="SimulationCompleted" />
                )}
              </button>
            ) : (
              <span className={`validate__status-action validate__status-action--${badgeClass}`} aria-hidden>
                {isDeleting ? (
                  <span className="validate__status-action__spinner" />
                ) : (
                  isDeploying && <StatusSvg status="deploying" />
                )}
              </span>
            )}
          </CustomToolTip>
        </div>
      </div>
      <div className="validate__table__td validate__table__td--right-cap" role="cell">
        <SessionActionsMenu session={session} />
      </div>
    </div>
  );
});

// ─── ValidateDetailsContent ───────────────────────────────────────────────────

function ValidateDetailsContent() {
  const {
    satelliteOptions,
    selectedSatellite,
    loadingSatellites,
    filteredSetups,
    loadingSetups,
    loadingSessions,
    filteredSessions,
    sessionsForSatellite,
    totalDurationMinutes,
    hasRunningSession,
    sessionFilterList,
    allFilters,
    deleteTarget,
  } = useValidateState();

  const {
    onSatelliteChange,
    setAllFilters,
    openCreateModal,
    onDeleteConfirm,
    onDeleteCancel,
    onSearchFilterApply,
    onStartNewSession,
  } = useValidateActions();

  const [nowMs, setNowMs] = useState(() => Date.now());
  const [setupDiagramTarget, setSetupDiagramTarget] = useState<ValidateSetup | null>(null);
  const [setupDiagramLoading, setSetupDiagramLoading] = useState(false);
  const [setupDiagramSubsystems, setSetupDiagramSubsystems] = useState<Array<{ name: string }>>([]);
  const navigate = useNavigate();
  const { mission_name, mission_id } = useUrlParams();
  useEffect(() => {
    if (!hasRunningSession) return;
    const id = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [hasRunningSession]);

  const totalSessionsCount = useMemo(
    () => sessionsForSatellite.length,
    [sessionsForSatellite],
  );

  const hasSetups = filteredSetups.length > 0;
  const hasSessionHistory = sessionsForSatellite.length > 0;
  const isFullLoading = loadingSatellites || ((loadingSetups || loadingSessions) && !hasSetups && !hasSessionHistory);
  const hasStaleCards = !isFullLoading && loadingSetups && filteredSetups.length > 0;
  const satelliteGroupId = selectedSatellite?.value?.groupId ?? '';
  const satelliteId = selectedSatellite?.value?.satId ?? '';

  const openSetupSubsystemDiagram = useCallback(async (setup: ValidateSetup) => {
    setSetupDiagramTarget(setup);
    setSetupDiagramSubsystems([]);
    setSetupDiagramLoading(true);

    try {
      const { data } = await getSetupSubsystemDiagram(
        mission_id ?? '',
        satelliteGroupId,
        satelliteId,
        setup.setupId,
      );
      setSetupDiagramSubsystems(data?.connectedSubsystems ?? []);
    } catch {
      setSetupDiagramSubsystems([]);
    } finally {
      setSetupDiagramLoading(false);
    }
  }, [mission_id, satelliteGroupId, satelliteId]);

  const missionSatellites = useSelector((state: { mission: MissionStore }) => state.mission.Satellites);
  const noSatellitesInMission = !loadingSatellites && satelliteOptions.length === 0 && (missionSatellites as unknown[]).length === 0;
  const allSatellitesPromoted = !loadingSatellites && satelliteOptions.length === 0 && (missionSatellites as unknown[]).length > 0;

  return (
    <>
      <div className="validate__details__page">
        {(noSatellitesInMission || allSatellitesPromoted) && (
          <div className="validate__empty-satellites">
            <EmptyPage
              icon={<NoSchedulePreview/>}
              content={noSatellitesInMission ? 'No Available Satellite' : 'Nothing to validate right now'}
              subContent={noSatellitesInMission
                ? 'Create Satellite in Mission to initate Validation'
                : 'All satellites in this mission have already been promoted to Operate. To create new validation setups, add a new satellite for Validate and it will appear here.'
              }
            />
            <button
              type="button"
              className="validate__create-satellite-btn"
              onClick={() => navigateTo(`${mission_name}/_details/${mission_id}/Satellites`, navigate)}
            >
              Create Satellite
            </button>
          </div>
        )}
        <div className="validate__container" style={noSatellitesInMission || allSatellitesPromoted ? { display: 'none' } : undefined}>
          <header className="validate__header">
            <div className="validate__header__left">
              <div className="validate__sat-pill validate__header__select">
                <SatelliteDropdownIcon className="validate__header__select__icon" size={18} />
                <CustomSelect
                  options={satelliteOptions}
                  placeholder={loadingSatellites ? 'Loading…' : 'Select'}
                  value={selectedSatellite}
                  onChange={onSatelliteChange}
                  isSearchable
                  isDisabled={loadingSatellites}
                  fullWidth={false}
                  minWidth="100px"
                  maxWidth="160px"
                  controllHeight={VALIDATE_HEADER_SELECT.controlHeight}
                  padding={VALIDATE_HEADER_SELECT.padding}
                  border={VALIDATE_HEADER_SELECT.border}
                  background={VALIDATE_HEADER_SELECT.background}
                  color={VALIDATE_HEADER_SELECT.color}
                />
              </div>
            </div>
          </header>

          <section className="validate__cards-section" aria-label="Setups">
            {/* Shimmer skeleton */}
            {isFullLoading && (
              <>
                <div className="validate__shimmer-cards-row" aria-hidden="true">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="validate__shimmer-card">
                      <div className="validate__shimmer-card__line validate__shimmer-card__line--sm" />
                      <div className="validate__shimmer-card__line validate__shimmer-card__line--lg" />
                    </div>
                  ))}
                  <div className="validate__shimmer-add" />
                </div>
                <div className="validate__shimmer-content">
                  <div className="validate__shimmer-toolbar" />
                  <div className="validate__shimmer-table">
                    {[1, 2, 3, 4].map((row) => (
                      <div key={row} className="validate__shimmer-table__row">
                        <div className="validate__shimmer-table__cell validate__shimmer-table__cell--left">
                          <span className="validate__shimmer-line validate__shimmer-line--md" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--sm" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--sm" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--sm" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--badge" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--pill" />
                        </div>
                        <div className="validate__shimmer-table__cell">
                          <span className="validate__shimmer-line validate__shimmer-line--badge" />
                        </div>
                        <div className="validate__shimmer-table__cell validate__shimmer-table__cell--right">
                          <span className="validate__shimmer-line validate__shimmer-line--icon" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Empty setups */}
            {!isFullLoading && selectedSatellite && !loadingSetups && !hasSetups && !hasSessionHistory && (
              <div className="validate__empty__setups">
                <span className="validate__empty__title">No setups available</span>
                <span className="validate__empty__description">
                  Create a new validation setup for this satellite to get started.
                </span>
                <button
                  className="validate__empty__add"
                  type="button"
                  onClick={openCreateModal}
                  aria-label="Create new setup"
                  data-testid="setup-empty-create-btn"
                >
                  <PlusIconSized size={16} />
                  <span className="validate__table__footer-text">Create new setup</span>
                </button>
              </div>
            )}

            {/* Real cards */}
            {!isFullLoading && hasSetups && (
              <>
                <span className='setup_lable'>SETUPS</span>
                <div className="validate__cards-row-wrapper">
                  <div
                    className={`validate__cards-row${hasStaleCards ? ' validate__cards-row--loading' : ''}`}
                    aria-label="Setups"
                  >
                    {filteredSetups.map((setup) => {
                      const isSetupBusy = setupHasRunningSession(setup);
                      return (
                        <div
                          key={setup.setupId}
                          className="validate__card"
                          role="group"
                          aria-label={`${setup.setupName} ${isSetupBusy ? 'busy' : 'not busy'}`}
                          data-testid={`setup-card-${setup.setupId}`}
                        >
                          <div className="validate__card__top">
                            <CustomToolTip
                              title={selectedSatellite?.label ?? 'Select'}
                              placement="top"
                              className="validate__card__satellite-label-tooltip"
                            >
                              <span className="validate__card__satellite-label" title={selectedSatellite?.label ?? 'Select'}>
                                {selectedSatellite?.label ?? 'Select'}
                              </span>
                            </CustomToolTip>
                            <span className="validate__card__status-slot" aria-hidden>
                              <span className={`validate__card__dot${isSetupBusy ? ' validate__card__dot--busy' : ''}`} />
                            </span>
                          </div>
                          <div className="validate__card__bottom">
                            <CustomToolTip title={setup.setupName} placement="top" className="validate__card__name-tooltip">
                              <span className="validate__card__name" title={setup.setupName} data-testid={`setup-name-${setup.setupId}`}>
                                {setup.setupName}
                              </span>
                            </CustomToolTip>
                            <div className="validate__card__actions">
                              <SetupCardActionsMenu
                                setup={setup}
                                isSetupBusy={isSetupBusy}
                                onOpenDiagram={openSetupSubsystemDiagram}
                              />
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    className="validate__add-card"
                    aria-label="Add new setup"
                    onClick={openCreateModal}
                    data-testid="setup-create-btn"
                  >
                    <PlusIconSized />
                  </button>
                </div>
              </>
            )}

            {!isFullLoading && selectedSatellite && (hasSetups || hasSessionHistory || loadingSessions) && (
              <>
                <div className="validate__content validate__fade-in">
                  <div className="validate__toolbar">
                    <h2 className="validate__toolbar__title">Sessions</h2>
                    <div className="validate__toolbar__trailing">
                      <button
                        type="button"
                        className="validate__new-session-btn"
                        aria-label="Create new session"
                        onClick={onStartNewSession}
                        data-testid="session-create-btn"
                      >
                        <PlusIconSized size={14} color="#F0F0F0" />
                        <span>New</span>
                      </button>
                      <div className="validate__toolbar__search">
                        <CustomFilter
                          alignment="right"
                          search={{ applyFilter: onSearchFilterApply }}
                          advanceFilter={{
                            filterIcon: <FilterSvg />,
                            filterList: sessionFilterList,
                            applyFilter: onSearchFilterApply,
                          }}
                          allFilters={allFilters}
                          setAllFilters={setAllFilters}
                          filterCase="validate"
                          boundingScreen="validate__details__page"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="validate__table" role="table" aria-label="Sessions">
                  <div className="validate__table__head" role="rowgroup">
                    <div className="validate__table__th" role="columnheader">Title</div>
                    <div className="validate__table__th" role="columnheader">Start Time</div>
                    <div className="validate__table__th" role="columnheader">
                      <CustomToolTip
                        title={totalDurationMinutes > 0 ? `${totalDurationMinutes} mins` : ''}
                        disabled={totalDurationMinutes <= 0}
                        placement="top"
                      >
                        <span>Duration</span>
                      </CustomToolTip>
                    </div>
                    <div className="validate__table__th" role="columnheader">Testing Environment</div>
                    <div className="validate__table__th" role="columnheader">Setup</div>
                    <div className="validate__table__th" role="columnheader">Teams</div>
                    <div className="validate__table__th" role="columnheader">Status</div>
                    <div className="validate__table__th" role="columnheader" aria-label="Actions" />
                  </div>

                  <div className="validate__table__body" role="rowgroup">
                    {!loadingSessions && filteredSessions.length === 0 && (
                      <div className="validate__table__empty validate__table__row" role="row">
                        {getEmptySessionsMessage(
                          totalSessionsCount,
                          Boolean((allFilters?.searchFilter ?? '').trim()),
                        )}
                      </div>
                    )}
                    {filteredSessions.map((session) => (
                      <SessionRow key={session.sessionId} session={session} nowMs={nowMs} />
                    ))}
                  </div>
                </div>
              </>
            )}
          </section>
        </div>
      </div>

      {/* Confirmation dialog — delete session / delete setup / end session */}
      {deleteTarget && (
        <ConfirmationDialog
          open
          type={deleteTarget.type === 'end-session' ? 'discard' : 'delete'}
          title={
            deleteTarget.type === 'end-session' ? 'End session?' :
            deleteTarget.type === 'session'     ? 'Delete session?' :
                                                  'Delete setup?'
          }
          description={
            deleteTarget.type === 'end-session'
              ? `Ending "${deleteTarget.name}" will stop the session immediately. This cannot be undone.`
              : deleteTarget.type === 'session'
              ? `Deleting "${deleteTarget.name}" will permanently remove it from this setup.`
              : `Deleting "${deleteTarget.name}" will permanently remove this setup and all its sessions.`
          }
          onConfirm={onDeleteConfirm}
          onCancel={onDeleteCancel}
          confirm_button_text={deleteTarget.type === 'end-session' ? 'End Session' : 'Delete'}
          dataTestIdPrefix={`validate-${deleteTarget.type}`}
        />
      )}

      <CreateSetupModal />
      <CreateSessionModal />
      <SubsystemDiagramModal
        open={Boolean(setupDiagramTarget)}
        title={setupDiagramLoading ? 'Loading Setup Diagram' : 'Setup Subsystem Diagram'}
        rootLabel={setupDiagramTarget?.setupName ?? 'Setup'}
        setupLabel={setupDiagramTarget?.setupName ?? 'Setup'}
        mode="setup"
        subsystems={setupDiagramSubsystems}
        loading={setupDiagramLoading}
        onClose={() => setSetupDiagramTarget(null)}
      />
    </>
  );
}

export default function ValidateDetails() {
  return (
    <ValidateProvider>
      <ValidateDetailsContent />
    </ValidateProvider>
  );
}
